/**
 * RFM Segmentation Service
 * 
 * Lógica de segmentação RFM 2D (Recência x Frequência) para visualização em matriz.
 * Cada célula (R, F) pertence a EXATAMENTE UM segmento, garantindo:
 * - Nenhuma duplicidade de leads entre segmentos
 * - Consistência visual no gráfico 2D
 * - Classificação clara e auditável
 * 
 * Matriz de Segmentação (5x5):
 * 
 *     R=1       R=2       R=3       R=4       R=5
 * F=5 Em Risco  Em Risco  Leais     Campeões  Campeões
 * F=4 Em Risco  Em Risco  Leais     Campeões  Campeões
 * F=3 Em Risco  Potenciais Potenciais Leais   Leais
 * F=2 Hibernando Hibernando Potenciais Recém   Recém
 * F=1 Hibernando Hibernando Outros    Recém   Recém
 * 
 * Onde:
 * - R (Recência): 1 = muito antigo, 5 = muito recente
 * - F (Frequência): 1 = baixa frequência, 5 = alta frequência
 */

// ============================================
// DEFINIÇÃO DOS SEGMENTOS RFM
// ============================================

/**
 * Segmentos RFM disponíveis
 * Cada segmento tem um ID único, nome de exibição, cor e descrição
 */
export const RFM_SEGMENTS = {
  CAMPEOES: {
    id: 'campeoes',
    nome: 'Campeões',
    nomeDisplay: 'Campeões',
    cssClass: 'champions',
    cor: '#28a745', // Verde
    corBadge: 'success',
    descricao: 'Compraram recentemente, compram frequentemente, gastam muito',
    acao: 'Recompensar e manter engajados',
  },
  CLIENTES_LEAIS: {
    id: 'clientes_leais',
    nome: 'Clientes Leais',
    nomeDisplay: 'Clientes Leais',
    cssClass: 'loyal',
    cor: '#007bff', // Azul
    corBadge: 'primary',
    descricao: 'Equilíbrio entre recência e frequência. Compraram recentemente e compram frequentemente.',
    acao: 'Manter satisfeitos e engajados',
  },
  POTENCIAIS_CAMPEOES: {
    id: 'potenciais_campeoes',
    nome: 'Potenciais Campeões',
    nomeDisplay: 'Potenciais Campeões',
    cssClass: 'potential',
    cor: '#17a2b8', // Ciano
    corBadge: 'info',
    descricao: 'Compraram recentemente, frequência moderada',
    acao: 'Incentivar aumento de frequência',
  },
  RECEM_CHEGADOS: {
    id: 'recem_chegados',
    nome: 'Recém Chegados',
    nomeDisplay: 'Recém Chegados',
    cssClass: 'new-customers',
    cor: '#6f42c1', // Roxo
    corBadge: 'info',
    descricao: 'Compraram recentemente, mas poucas vezes',
    acao: 'Nutrir relacionamento, criar fidelização',
  },
  EM_RISCO: {
    id: 'em_risco',
    nome: 'Em Risco',
    nomeDisplay: 'Em Risco',
    cssClass: 'at-risk',
    cor: '#fd7e14', // Laranja
    corBadge: 'warning',
    descricao: 'Compravam frequentemente, mas não compram há tempos',
    acao: 'Campanhas urgentes de reengajamento',
  },
  HIBERNANDO: {
    id: 'hibernando',
    nome: 'Hibernando/Perdidos',
    nomeDisplay: 'Hibernando/Perdidos',
    cssClass: 'about-to-sleep',
    cor: '#dc3545', // Vermelho
    corBadge: 'danger',
    descricao: 'Não compram há muito tempo, compraram poucas vezes',
    acao: 'Campanhas de reativação ou remoção da base',
  },
  OUTROS: {
    id: 'outros',
    nome: 'Outros',
    nomeDisplay: 'Outros',
    cssClass: 'promising',
    cor: '#6c757d', // Cinza
    corBadge: 'secondary',
    descricao: 'Não se encaixam claramente em outros segmentos',
    acao: 'Análise individual ou campanhas genéricas',
  },
}

// ============================================
// MATRIZ DE SEGMENTAÇÃO 2D (R x F)
// ============================================

/**
 * Matriz de segmentação 2D definitiva
 * Índice: [frequencia][recencia] onde frequencia e recencia vão de 1 a 5
 * Cada célula contém o ID do segmento
 * 
 * IMPORTANTE: Esta é a ÚNICA fonte de verdade para segmentação 2D
 */
const SEGMENTATION_MATRIX = {
  // Frequência 5 (alta)
  '5-1': 'EM_RISCO',
  '5-2': 'EM_RISCO',
  '5-3': 'CLIENTES_LEAIS',
  '5-4': 'CAMPEOES',
  '5-5': 'CAMPEOES',

  // Frequência 4
  '4-1': 'EM_RISCO',
  '4-2': 'EM_RISCO',
  '4-3': 'CLIENTES_LEAIS',
  '4-4': 'CAMPEOES',
  '4-5': 'CAMPEOES',

  // Frequência 3
  '3-1': 'EM_RISCO',
  '3-2': 'POTENCIAIS_CAMPEOES',
  '3-3': 'POTENCIAIS_CAMPEOES',
  '3-4': 'CLIENTES_LEAIS',
  '3-5': 'CLIENTES_LEAIS',

  // Frequência 2
  '2-1': 'HIBERNANDO',
  '2-2': 'HIBERNANDO',
  '2-3': 'POTENCIAIS_CAMPEOES',
  '2-4': 'RECEM_CHEGADOS',
  '2-5': 'RECEM_CHEGADOS',

  // Frequência 1 (baixa)
  '1-1': 'HIBERNANDO',
  '1-2': 'HIBERNANDO',
  '1-3': 'OUTROS',
  '1-4': 'RECEM_CHEGADOS',
  '1-5': 'RECEM_CHEGADOS',
}

// ============================================
// FUNÇÕES DE SEGMENTAÇÃO
// ============================================

/**
 * Determina o segmento de um cliente baseado em R e F
 * Esta é a função principal de segmentação 2D
 * 
 * @param {number} recencia - Score de recência (1-5)
 * @param {number} frequencia - Score de frequência (1-5)
 * @returns {Object} Objeto do segmento com todas as propriedades
 */
export function determinarSegmento(recencia, frequencia) {
  // Validar inputs
  const r = Math.max(1, Math.min(5, Math.round(recencia || 1)))
  const f = Math.max(1, Math.min(5, Math.round(frequencia || 1)))

  // Criar chave para lookup na matriz
  const key = `${f}-${r}`

  // Buscar segmento na matriz
  const segmentoId = SEGMENTATION_MATRIX[key] || 'OUTROS'

  return RFM_SEGMENTS[segmentoId]
}

/**
 * Determina o segmento pelo nome (para compatibilidade com dados do backend)
 * 
 * @param {string} nomeSegmento - Nome do segmento vindo do backend
 * @returns {Object} Objeto do segmento normalizado
 */
export function getSegmentoPorNome(nomeSegmento) {
  if (!nomeSegmento) return RFM_SEGMENTS.OUTROS

  // Normalização de nomes do backend
  const normalizacao = {
    // Português
    Campeões: 'CAMPEOES',
    campeoes: 'CAMPEOES',
    'Clientes Leais': 'CLIENTES_LEAIS',
    clientes_leais: 'CLIENTES_LEAIS',
    'Potenciais Campeões': 'POTENCIAIS_CAMPEOES',
    potenciais_campeoes: 'POTENCIAIS_CAMPEOES',
    'Recém Chegados': 'RECEM_CHEGADOS',
    'Recém-Chegados': 'RECEM_CHEGADOS',
    'Recem Chegados': 'RECEM_CHEGADOS',
    'Recem-Chegados': 'RECEM_CHEGADOS',
    recem_chegados: 'RECEM_CHEGADOS',
    'Em Risco': 'EM_RISCO',
    em_risco: 'EM_RISCO',
    'Hibernando/Perdidos': 'HIBERNANDO',
    'Hibernando / Perdidos': 'HIBERNANDO',
    hibernando_perdidos: 'HIBERNANDO',
    Outros: 'OUTROS',
    outros: 'OUTROS',
    'Grandes Gastadores': 'OUTROS', // Mapeado para Outros na visualização 2D
    grandes_gastadores: 'OUTROS',

    // Inglês (compatibilidade)
    Champions: 'CAMPEOES',
    'Loyal Customers': 'CLIENTES_LEAIS',
    'Potential Loyalists': 'POTENCIAIS_CAMPEOES',
    'New Customers': 'RECEM_CHEGADOS',
    'At Risk': 'EM_RISCO',
    Lost: 'HIBERNANDO',
    Others: 'OUTROS',
    'Cannot Lose Them': 'OUTROS',
  }

  const segmentoId = normalizacao[nomeSegmento] || 'OUTROS'
  return RFM_SEGMENTS[segmentoId]
}

/**
 * Retorna a classe CSS para um segmento
 * 
 * @param {string} nomeSegmento - Nome do segmento
 * @returns {string} Classe CSS
 */
export function getSegmentClass(nomeSegmento) {
  const segmento = getSegmentoPorNome(nomeSegmento)
  return segmento.cssClass
}

/**
 * Retorna a classe CSS para uma célula da matriz
 * 
 * @param {number} recencia - Score de recência (1-5)
 * @param {number} frequencia - Score de frequência (1-5)
 * @returns {string} Classe CSS
 */
export function getCellClass(recencia, frequencia) {
  const segmento = determinarSegmento(recencia, frequencia)
  return segmento.cssClass
}

/**
 * Retorna o nome de exibição do segmento
 * 
 * @param {string} nomeSegmento - Nome do segmento
 * @returns {string} Nome para exibição
 */
export function getSegmentDisplayName(nomeSegmento) {
  const segmento = getSegmentoPorNome(nomeSegmento)
  return segmento.nomeDisplay
}

/**
 * Retorna o label formatado para células RFM (com quebra de linha)
 * 
 * @param {string} nomeSegmento - Nome do segmento
 * @returns {string} Label com quebra de linha
 */
export function getSegmentLabel(nomeSegmento) {
  const segmento = getSegmentoPorNome(nomeSegmento)
  const nome = segmento.nomeDisplay

  // Adicionar quebra de linha para nomes compostos
  if (nome.includes(' ')) {
    const palavras = nome.split(' ')
    if (palavras.length === 2) {
      return palavras.join('\n')
    }
    // Para 3 ou mais palavras, quebrar no meio
    const meio = Math.ceil(palavras.length / 2)
    return `${palavras.slice(0, meio).join(' ')}\n${palavras.slice(meio).join(' ')}`
  }
  return nome
}

/**
 * Retorna a variante de cor para badges
 * 
 * @param {string} nomeSegmento - Nome do segmento
 * @returns {string} Variante Bootstrap (success, primary, etc.)
 */
export function getSegmentBadgeVariant(nomeSegmento) {
  const segmento = getSegmentoPorNome(nomeSegmento)
  return segmento.corBadge
}

/**
 * Retorna a cor hex do segmento
 * 
 * @param {string} nomeSegmento - Nome do segmento
 * @returns {string} Cor em formato hex
 */
export function getSegmentColor(nomeSegmento) {
  const segmento = getSegmentoPorNome(nomeSegmento)
  return segmento.cor
}

// ============================================
// PROCESSAMENTO DE DADOS RFM
// ============================================

/**
 * Processa a matriz RFM a partir dos dados da API
 * Garante que cada cliente seja classificado em apenas um segmento
 * 
 * @param {Object} dadosAPI - Dados da API (matriz_rfm, clientes)
 * @returns {Object} Matriz processada com clientes e segmentos
 */
export function processarMatrizRFM(dadosAPI) {
  // Inicializar matriz 5x5 (R x F)
  const matriz = {}
  for (let r = 1; r <= 5; r++) {
    for (let f = 1; f <= 5; f++) {
      const key = `${r}-${f}`
      const segmento = determinarSegmento(r, f)
      matriz[key] = {
        recencia: r,
        frequencia: f,
        clientes: [],
        total: 0,
        segmento: segmento.nome,
        segmentoId: segmento.id,
        cssClass: segmento.cssClass,
      }
    }
  }

  // Processar matriz_frequencia_recencia da API (se disponível)
  if (dadosAPI?.matriz_rfm?.matriz_frequencia_recencia) {
    const matrizFR = dadosAPI.matriz_rfm.matriz_frequencia_recencia

    // A matriz vem: [F-1][R-1] onde F vai de 1 a 5 e R vai de 1 a 5
    for (let fIndex = 0; fIndex < 5; fIndex++) {
      for (let rIndex = 0; rIndex < 5; rIndex++) {
        const f = fIndex + 1
        const r = rIndex + 1
        const key = `${r}-${f}`
        const total = matrizFR[fIndex]?.[rIndex] || 0

        if (matriz[key]) {
          matriz[key].total = total
        }
      }
    }
  }

  // Distribuir clientes na matriz
  if (dadosAPI?.clientes && Array.isArray(dadosAPI.clientes)) {
    dadosAPI.clientes.forEach(cliente => {
      if (cliente.rfm_scores) {
        const r = cliente.rfm_scores.r || 1
        const f = cliente.rfm_scores.f || 1
        const key = `${r}-${f}`

        if (matriz[key]) {
          // Determinar segmento baseado APENAS em R e F (2D)
          const segmento = determinarSegmento(r, f)

          // Atualizar segmento do cliente para consistência
          const clienteProcessado = {
            ...cliente,
            segmento: segmento.nome,
            segmento_2d: segmento.nome, // Segmento para visualização 2D
          }

          matriz[key].clientes.push(clienteProcessado)
        }
      }
    })
  }

  // Processar clientes_por_segmento (se disponível)
  if (dadosAPI?.clientes_por_segmento) {
    Object.entries(dadosAPI.clientes_por_segmento).forEach(([chaveSegmento, dados]) => {
      if (dados?.clientes && Array.isArray(dados.clientes)) {
        dados.clientes.forEach(cliente => {
          if (cliente.rfm_scores) {
            const r = cliente.rfm_scores.r || 1
            const f = cliente.rfm_scores.f || 1
            const key = `${r}-${f}`

            if (matriz[key]) {
              // Verificar se cliente já existe
              const clienteExiste = matriz[key].clientes.some(
                c => c.cliente_store_id === cliente.cliente_store_id,
              )

              if (!clienteExiste) {
                const segmento = determinarSegmento(r, f)
                const clienteProcessado = {
                  ...cliente,
                  segmento: segmento.nome,
                  segmento_2d: segmento.nome,
                }
                matriz[key].clientes.push(clienteProcessado)
              }
            }
          }
        })
      }
    })
  }

  return matriz
}

/**
 * Calcula os totais por segmento a partir da matriz
 * 
 * @param {Object} matriz - Matriz RFM processada
 * @returns {Object} Totais por segmento
 */
export function calcularTotaisPorSegmento(matriz) {
  const totais = {}

  // Inicializar todos os segmentos com 0
  Object.values(RFM_SEGMENTS).forEach(seg => {
    totais[seg.nome] = 0
  })

  // Somar totais de cada célula
  Object.values(matriz).forEach(celula => {
    if (celula.segmento && celula.total > 0) {
      totais[celula.segmento] = (totais[celula.segmento] || 0) + celula.total
    }
  })

  // Remover segmentos com 0 clientes
  return Object.fromEntries(
    Object.entries(totais).filter(([, total]) => total > 0),
  )
}

/**
 * Obtém todos os clientes de um segmento específico
 * 
 * @param {Object} matriz - Matriz RFM processada
 * @param {string} nomeSegmento - Nome do segmento
 * @returns {Array} Lista de clientes do segmento
 */
export function getClientesPorSegmento(matriz, nomeSegmento) {
  const segmento = getSegmentoPorNome(nomeSegmento)
  const clientes = []

  Object.values(matriz).forEach(celula => {
    if (celula.segmento === segmento.nome) {
      clientes.push(...celula.clientes)
    }
  })

  // Remover duplicatas por cliente_store_id
  const clientesUnicos = []
  const idsVistos = new Set()

  clientes.forEach(cliente => {
    if (!idsVistos.has(cliente.cliente_store_id)) {
      idsVistos.add(cliente.cliente_store_id)
      clientesUnicos.push(cliente)
    }
  })

  return clientesUnicos
}

/**
 * Verifica se há duplicidade de clientes entre segmentos
 * Função de debug/auditoria
 * 
 * @param {Object} matriz - Matriz RFM processada
 * @returns {Object} Relatório de duplicidades
 */
export function verificarDuplicidades(matriz) {
  const clientesPorSegmento = {}
  const duplicidades = []

  // Coletar clientes por segmento
  Object.values(matriz).forEach(celula => {
    if (!clientesPorSegmento[celula.segmento]) {
      clientesPorSegmento[celula.segmento] = new Set()
    }
    celula.clientes.forEach(cliente => {
      clientesPorSegmento[celula.segmento].add(cliente.cliente_store_id)
    })
  })

  // Verificar duplicidades entre segmentos
  const segmentos = Object.keys(clientesPorSegmento)
  for (let i = 0; i < segmentos.length; i++) {
    for (let j = i + 1; j < segmentos.length; j++) {
      const seg1 = segmentos[i]
      const seg2 = segmentos[j]
      const clientes1 = clientesPorSegmento[seg1]
      const clientes2 = clientesPorSegmento[seg2]

      clientes1.forEach(clienteId => {
        if (clientes2.has(clienteId)) {
          duplicidades.push({
            clienteId,
            segmentos: [seg1, seg2],
          })
        }
      })
    }
  }

  return {
    temDuplicidades: duplicidades.length > 0,
    duplicidades,
    totalPorSegmento: Object.fromEntries(
      Object.entries(clientesPorSegmento).map(([seg, clientes]) => [seg, clientes.size]),
    ),
  }
}

// ============================================
// EXPORTAÇÕES ADICIONAIS PARA COMPATIBILIDADE
// ============================================

/**
 * Normaliza nome de segmento vindo do backend
 * Mantido para compatibilidade com código existente
 */
export function normalizarSegmento(segmento) {
  if (!segmento) return 'Outros'
  const seg = getSegmentoPorNome(segmento)
  return seg.nome
}

/**
 * Retorna variante de cor para elementos
 */
export function getSegmentColorVariant(segmento) {
  return getSegmentBadgeVariant(segmento)
}

/**
 * Retorna badge para recência
 */
export function getBadgeRecencia(dias) {
  if (!dias) return { variant: 'light', texto: 'N/A' }
  if (dias <= 30) return { variant: 'success', texto: `${dias} dias` }
  if (dias <= 90) return { variant: 'warning', texto: `${dias} dias` }
  return { variant: 'danger', texto: `${dias} dias` }
}

// Exportar todos os segmentos para uso externo
export const SEGMENTOS = RFM_SEGMENTS
export const MATRIZ_SEGMENTACAO = SEGMENTATION_MATRIX
